import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import HondaLoader from '../../components/common/HondaLoader';
import FianceCard from '../../components/common/FianceCard';
import { validateBuyerLink, storeBuyerInformation } from '../../services/cesionesService';
import { formatDateInput, formatCurrencyInput } from '../../utils/inputValidation';

// ─── Constants ─────────────────────────────────────────────────────────────
const PROVINCIAS = [
  'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba',
  'Corrientes', 'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja',
  'Mendoza', 'Misiones', 'Neuquén', 'Río Negro', 'Salta', 'San Juan',
  'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán',
];

const TIPOS_EMPRESA = [
  'Sociedad Anónima',
  'Sociedad de Responsabilidad Limitada',
  'Sociedad Colectiva',
  'Cooperativa',
  'Otro',
];

const CATEGORIA_FISCAL_OPT = [
  'IVA Responsable Inscripto',
  'IVA Sujeto Exento',
  'Responsable Monotributo',
  'Consumidor Final',
  'No Responsable',
];

const TIPO_INGRESO_OPT = [
  'Relación de Dependencia',
  'Monotributo',
  'Responsable Inscripto',
  'Otro',
];

const ORIGEN_FUENTES = ['Mis Ingresos', 'Cónyuge', 'Padre', 'Madre', 'Hermano'];

// ─── Payload helpers ────────────────────────────────────────────────────────
const convertDate = (ddmmyyyy) => {
  if (!ddmmyyyy || typeof ddmmyyyy !== 'string') return null;
  const parts = ddmmyyyy.split('/');
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy || yyyy.length !== 4) return null;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
};

const mapGender = (g) => {
  if (g === 'M') return '1 - Masculino';
  if (g === 'F') return '2 - Femenino';
  return null;
};

const mapMarriageStatus = (s) => {
  if (s === 'Soltero') return '1 - Soltero';
  if (s === 'Casado') return '2 - Casado';
  return null;
};

const stripCurrency = (value) => {
  if (!value) return null;
  const stripped = value.toString().replace(/\./g, '').replace(/,/g, '.');
  return stripped || null;
};

const COMMON_REQUIRED_FIELDS = [
  ['calle', 'Calle'],
  ['numero', 'Número'],
  ['localidad', 'Localidad'],
  ['codigoPostal', 'Código postal'],
  ['provincia', 'Provincia'],
  ['pais', 'País'],
  ['telefono', 'Teléfono particular'],
  ['email', 'Email'],
];

const SPOUSE_REQUIRED_FIELDS = (prefix) => [
  [`${prefix}CompleteName`, 'Nombre completo del cónyuge'],
  [`${prefix}DocumentNumber`, 'Documento del cónyuge'],
  [`${prefix}BirthDate`, 'Fecha de nacimiento del cónyuge'],
  [`${prefix}Gender`, 'Género del cónyuge'],
  [`${prefix}Nationality`, 'Nacionalidad del cónyuge'],
  [`${prefix}PEP`, 'PEP del cónyuge'],
];

const getRequiredFields = (form) => {
  const fields = [...COMMON_REQUIRED_FIELDS];

  if (form.tipoPersona === 'FISICA') {
    fields.push(
      ['dniCuit', 'DNI / CUIT'],
      ['nombreCompleto', 'Nombre completo'],
      ['fechaNacimiento', 'Fecha de nacimiento'],
      ['genero', 'Género / sexo'],
      ['nacionalidad', 'Nacionalidad'],
      ['estadoCivil', 'Estado civil'],
      ['pep', 'PEP'],
      ['sujetoObligado', 'Sujeto obligado'],
      ['categoriaFiscal', 'Categoría fiscal'],
      ['tipoIngreso', 'Tipo de ingreso'],
      ['rubroActividad', 'Rubro / actividad'],
      ['ingresosNetos', 'Ingresos netos mensuales'],
      ['ingresosNetosTotal', 'Ingresos netos totales'],
    );

    if (form.estadoCivil === 'Casado') {
      fields.push(...SPOUSE_REQUIRED_FIELDS('conyuge'));
    }
  }

  if (form.tipoPersona === 'JURIDICA') {
    fields.push(
      ['dniCuit', 'DNI / CUIT'],
      ['nombreEmpresa', 'Nombre empresa'],
      ['fechaInscripcion', 'Fecha de inscripción'],
      ['tipoEmpresa', 'Tipo de empresa'],
      ['pep', 'PEP'],
      ['sujetoObligado', 'Sujeto obligado'],
      ['ingresosNetos', 'Ingresos comprador'],
      ['apodNombre', 'Nombre completo del apoderado'],
      ['apodDni', 'DNI del apoderado'],
      ['apodFechaNacimiento', 'Fecha de nacimiento del apoderado'],
      ['apodGenero', 'Género / sexo del apoderado'],
      ['apodNacionalidad', 'Nacionalidad del apoderado'],
      ['apodEstadoCivil', 'Estado civil del apoderado'],
      ['apodPep', 'PEP del apoderado'],
      ['apodSujetoObligado', 'Sujeto obligado del apoderado'],
      ['apodCalle', 'Calle del apoderado'],
      ['apodNumero', 'Número del apoderado'],
      ['apodLocalidad', 'Localidad del apoderado'],
      ['apodCodigoPostal', 'Código postal del apoderado'],
      ['apodProvincia', 'Provincia del apoderado'],
      ['apodPais', 'País del apoderado'],
      ['apodTelefono', 'Teléfono particular del apoderado'],
      ['apodEmail', 'Email del apoderado'],
    );

    if (form.apodEstadoCivil === 'Casado') {
      fields.push(...SPOUSE_REQUIRED_FIELDS('apodConyuge'));
    }
  }

  return fields;
};

const validateRequiredFields = (form) => {
  const missingFields = getRequiredFields(form)
    .filter(([name]) => !String(form[name] ?? '').trim())
    .map(([, label]) => label);

  return missingFields.length > 0
    ? `Complete los campos obligatorios: ${missingFields.join(', ')}.`
    : null;
};

const buildPayload = (form, validationData) => {
  const group = validationData?.group || '';
  const order = validationData?.order || '';

  if (form.tipoPersona === 'FISICA') {
    const hasSpouse = form.estadoCivil === 'Casado';
    return {
      group,
      order,
      personType: '1 - Persona Física',
      completeName: form.nombreCompleto || null,
      documentNumber: form.dniCuit || null,
      personBirthDate: convertDate(form.fechaNacimiento),
      personMarriageStatus: mapMarriageStatus(form.estadoCivil),
      personGender: mapGender(form.genero),
      personNationality: form.nacionalidad || null,
      personIncomeType: form.tipoIngreso || null,
      personActivity: form.rubroActividad || null,
      personAfipCodeActivity: form.codigoActividadAfip || null,
      personlaborTime: form.antiguedadLaboral || null,
      personEmployeeName: form.nombreEmpleador || null,
      personEmployeeAddress: form.domicilioEmpleador || null,
      personEmployeeIdent: form.cuitEmpleador || null,
      personEmployeePhone: form.telefonoEmpleador || null,
      personEmployeeMail: form.emailEmpleador || null,
      taxCategory: form.categoriaFiscal || null,
      address: form.calle || null,
      addressNumber: form.numero || null,
      addressFloor: form.piso || null,
      addressDpto: form.depto || null,
      addressLocale: form.localidad || null,
      addressPostalCode: form.codigoPostal || null,
      addressProvince: form.provincia || null,
      income: stripCurrency(form.ingresosNetos),
      obligated: form.sujetoObligado || null,
      pep: form.pep || null,
      mainPhone: form.telefono || null,
      secondaryPhone: form.telefonoAlt || '',
      mail: form.email || null,
      incomeSources: {
        own: form.origenFondos?.['Mis Ingresos'] ? 'SI' : 'NO',
        fiance: form.origenFondos?.['Cónyuge'] ? 'SI' : 'NO',
        father: form.origenFondos?.['Padre'] ? 'SI' : 'NO',
        mother: form.origenFondos?.['Madre'] ? 'SI' : 'NO',
        brother: form.origenFondos?.['Hermano'] ? 'SI' : 'NO',
        incomeTotals: stripCurrency(form.ingresosNetosTotal),
      },
      fianceCompleteName: hasSpouse ? (form.conyugeCompleteName || null) : null,
      fianceBirthDate: hasSpouse ? convertDate(form.conyugeBirthDate) : null,
      fianceDocumentNumber: hasSpouse ? (form.conyugeDocumentNumber || null) : null,
      fianceGender: hasSpouse ? (form.conyugeGender || null) : null,
      fiancePEP: hasSpouse ? (form.conyugePEP || null) : null,
      companyDate: null,
      apodPersonCompleteName: null,
      apodPersonBirthDate: null,
      apodPersonMarriageStatus: null,
      apodPersonGender: null,
      apodPersonDocumentNumber: null,
      apodAddress: null,
      apodAddressNumber: null,
      apodAddressFloor: null,
      apodAddressDpto: null,
      apodAddressPostalCode: null,
      apodAddressLocale: null,
      apodAddressProvince: null,
      apodPersonNationality: null,
      apodPersonPhone: null,
      apodPersonMail: null,
      apodPep: null,
      apodFianceCompleteName: null,
      apodFianceBirthDate: null,
      apodFianceDocumentNumber: null,
      apodFianceGender: null,
      apodFianceObligated: null,
      apodFiancePEP: null,
    };
  }

  // Persona Jurídica
  const hasApodSpouse = form.apodEstadoCivil === 'Casado';
  return {
    group,
    order,
    personType: '2 - Persona Jurídica',
    completeName: form.nombreEmpresa || null,
    documentNumber: form.dniCuit || null,
    companyDate: convertDate(form.fechaInscripcion),
    address: form.calle || null,
    addressNumber: form.numero || null,
    addressFloor: form.piso || null,
    addressDpto: form.depto || null,
    addressLocale: form.localidad || null,
    addressPostalCode: form.codigoPostal || null,
    addressProvince: form.provincia || null,
    income: stripCurrency(form.ingresosNetos),
    obligated: form.sujetoObligado || null,
    pep: form.pep || null,
    mainPhone: form.telefono || null,
    secondaryPhone: form.telefonoAlt || '',
    mail: form.email || null,
    personBirthDate: null,
    personMarriageStatus: null,
    personGender: null,
    personNationality: null,
    personIncomeType: null,
    personActivity: null,
    personAfipCodeActivity: null,
    personlaborTime: null,
    personEmployeeName: null,
    personEmployeeAddress: null,
    personEmployeeIdent: null,
    personEmployeePhone: null,
    personEmployeeMail: null,
    taxCategory: null,
    fianceCompleteName: null,
    fianceBirthDate: null,
    fianceDocumentNumber: null,
    fianceGender: null,
    fiancePEP: null,
    incomeSources: null,
    apodPersonCompleteName: form.apodNombre || null,
    apodPersonBirthDate: convertDate(form.apodFechaNacimiento),
    apodPersonMarriageStatus: mapMarriageStatus(form.apodEstadoCivil),
    apodPersonGender: mapGender(form.apodGenero),
    apodPersonDocumentNumber: form.apodDni || null,
    apodAddress: form.apodCalle || null,
    apodAddressNumber: form.apodNumero || null,
    apodAddressFloor: form.apodPiso || null,
    apodAddressDpto: form.apodDepto || null,
    apodAddressPostalCode: form.apodCodigoPostal || null,
    apodAddressLocale: form.apodLocalidad || null,
    apodAddressProvince: form.apodProvincia || null,
    apodPersonNationality: form.apodNacionalidad || null,
    apodPersonPhone: form.apodTelefono || null,
    apodPersonMail: form.apodEmail || null,
    apodPep: form.apodPep || null,
    apodFianceCompleteName: hasApodSpouse ? (form.apodConyugeCompleteName || null) : null,
    apodFianceBirthDate: hasApodSpouse ? convertDate(form.apodConyugeBirthDate) : null,
    apodFianceDocumentNumber: hasApodSpouse ? (form.apodConyugeDocumentNumber || null) : null,
    apodFianceGender: hasApodSpouse ? (form.apodConyugeGender || null) : null,
    apodFiancePEP: hasApodSpouse ? (form.apodConyugePEP || null) : null,
  };
};

// ─── UI Components ─────────────────────────────────────────────────────────
const Card = ({ title, icon, children }) => (
  <div className="mb-6 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group">
    <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300" />
    <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
      <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
        <span className="material-icons text-honda-600">{icon}</span>
        {title}
      </h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Lbl = ({ children, required }) => (
  <label className="block text-xs font-medium text-gray-500 mb-1">
    {children}{required && <span className="text-honda-600"> *</span>}
  </label>
);

const INP_CLASS = "w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm bg-gray-50 focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 placeholder-gray-400 transition-all duration-200";

const Inp = (props) => (
  <input
    {...props}
    className={INP_CLASS}
  />
);

const Sel = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm bg-gray-50 focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
  >
    {children}
  </select>
);

// ─── Shared DOMICILIO section ───────────────────────────────────────────────
const DomicilioSection = ({ form, onChange }) => (
  <Card title="DOMICILIO" icon="location_on">
    <div className="space-y-3">
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-4">
          <Lbl required>CALLE</Lbl>
          <Inp name="calle" value={form.calle || ''} onChange={onChange} />
        </div>
        <div className="col-span-2">
          <Lbl required>NÚMERO</Lbl>
          <Inp name="numero" value={form.numero || ''} onChange={onChange} />
        </div>
        <div className="col-span-1">
          <Lbl>PISO</Lbl>
          <Inp name="piso" value={form.piso || ''} onChange={onChange} />
        </div>
        <div className="col-span-1">
          <Lbl>DEPTO</Lbl>
          <Inp name="depto" value={form.depto || ''} onChange={onChange} />
        </div>
        <div className="col-span-4">
          <Lbl required>LOCALIDAD</Lbl>
          <Inp name="localidad" value={form.localidad || ''} onChange={onChange} />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          <Lbl required>CÓDIGO POSTAL</Lbl>
          <Inp name="codigoPostal" value={form.codigoPostal || ''} onChange={onChange} />
        </div>
        <div className="col-span-5">
          <Lbl required>PROVINCIA</Lbl>
          <Sel name="provincia" value={form.provincia || ''} onChange={onChange}>
            <option value="">Seleccione...</option>
            {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
          </Sel>
        </div>
        <div className="col-span-4">
          <Lbl required>PAÍS</Lbl>
          <Inp name="pais" value={form.pais || ''} onChange={onChange} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Lbl required>TELÉFONO PARTICULAR</Lbl>
          <Inp name="telefono" value={form.telefono || ''} onChange={onChange} placeholder="Ej: 011 1234-5678" />
        </div>
        <div>
          <Lbl>TELÉFONO ALTERNATIVO</Lbl>
          <Inp name="telefonoAlt" value={form.telefonoAlt || ''} onChange={onChange} placeholder="Ej: 011 1234-5678" />
        </div>
        <div>
          <Lbl required>EMAIL</Lbl>
          <Inp name="email" value={form.email || ''} onChange={onChange} placeholder="ejemplo@gmail.com" type="email" />
        </div>
      </div>
    </div>
  </Card>
);

// ─── MoneyInput ─────────────────────────────────────────────────────────────
const MoneyInput = ({ name, value, onChange }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">$</span>
    <input
      name={name}
      value={value || ''}
      onChange={onChange}
      className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-md text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
    />
  </div>
);

// ─── PERSONA FÍSICA FORM ────────────────────────────────────────────────────
const PersonaFisicaForm = ({ form, onChange, onDate, onCurrency, onCheckbox, onTipoPersonaChange, setForm }) => (
  <>
    {/* DATOS DEL COMPRADOR */}
    <Card title="DATOS DEL COMPRADOR" icon="person">
      <div className="space-y-3">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <Lbl>TIPO DE PERSONA</Lbl>
            <Sel name="tipoPersona" value={form.tipoPersona || ''} onChange={onTipoPersonaChange}>
              <option value="">Seleccione...</option>
              <option value="FISICA">Persona Física</option>
              <option value="JURIDICA">Persona Jurídica</option>
            </Sel>
          </div>
          <div className="col-span-8">
            <Lbl required>DNI / CUIT</Lbl>
            <Inp name="dniCuit" value={form.dniCuit || ''} onChange={onChange} />
          </div>
        </div>
        <div>
          <Lbl required>NOMBRE COMPLETO</Lbl>
          <Inp name="nombreCompleto" value={form.nombreCompleto || ''} onChange={onChange} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl required>FECHA DE NACIMIENTO</Lbl>
            <Inp name="fechaNacimiento" value={form.fechaNacimiento || ''} onChange={onDate} placeholder="dd/mm/aaaa" maxLength="10" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Lbl required>GÉNERO / SEXO</Lbl>
              <span className="text-xs text-red-600">
                (Como figura en el documento)
              </span>
            </div>

            <Sel
              name="genero"
              value={form.genero || ''}
              onChange={onChange}
            >
              <option value="">Seleccione...</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </Sel>
          </div>
          <div>
            <Lbl required>NACIONALIDAD</Lbl>
            <Inp name="nacionalidad" value={form.nacionalidad || ''} onChange={onChange} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl required>ESTADO CIVIL</Lbl>
            <Sel name="estadoCivil" value={form.estadoCivil || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              <option value="Soltero">Soltero/a</option>
              <option value="Casado">Casado/a</option>
            </Sel>
          </div>
          <div>
            <Lbl required>PEP (PERSONA EXPUESTA POLÍTICAMENTE)</Lbl>
            <Sel name="pep" value={form.pep || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              <option value="SI">Sí</option>
              <option value="NO">No</option>
            </Sel>
          </div>
          <div>
            <Lbl required>SUJETO OBLIGADO</Lbl>
            <Sel name="sujetoObligado" value={form.sujetoObligado || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              <option value="SI">Sí</option>
              <option value="NO">No</option>
            </Sel>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl required>CATEGORÍA FISCAL</Lbl>
            <Sel name="categoriaFiscal" value={form.categoriaFiscal || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              {CATEGORIA_FISCAL_OPT.map(c => <option key={c} value={c}>{c}</option>)}
            </Sel>
          </div>
        </div>
      </div>
    </Card>

    {/* DATOS DEL CÓNYUGE - solo si estado civil es Casado */}
    {form.estadoCivil === 'Casado' && (
      <FianceCard data={form} setData={setForm} prefix="conyuge" />
    )}

    {/* DOMICILIO */}
    <DomicilioSection form={form} onChange={onChange} />

    {/* DATOS LABORALES */}
    <Card title="DATOS LABORALES" icon="work">
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl required>TIPO DE INGRESO</Lbl>
            <Sel name="tipoIngreso" value={form.tipoIngreso || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              {TIPO_INGRESO_OPT.map(t => <option key={t} value={t}>{t}</option>)}
            </Sel>
          </div>
          <div>
            <Lbl>CÓDIGO DE ACTIVIDAD AFIP</Lbl>
            <Inp name="codigoActividadAfip" value={form.codigoActividadAfip || ''} onChange={onChange} />
          </div>
          <div>
            <Lbl required>RUBRO / ACTIVIDAD</Lbl>
            <Inp name="rubroActividad" value={form.rubroActividad || ''} onChange={onChange} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl>ANTIGÜEDAD LABORAL</Lbl>
            <Inp name="antiguedadLaboral" value={form.antiguedadLaboral || ''} onChange={onChange} placeholder="Ej: 5 años" />
          </div>
          <div>
            <Lbl required>INGRESOS NETOS MENSUALES $</Lbl>
            <MoneyInput name="ingresosNetos" value={form.ingresosNetos} onChange={onCurrency} />
          </div>
        </div>
      </div>
    </Card>

    <Card title="DATOS DEL EMPLEADOR" icon="business_center">
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl>NOMBRE EMPLEADOR / RAZÓN SOCIAL</Lbl>
            <Inp name="nombreEmpleador" value={form.nombreEmpleador || ''} onChange={onChange} />
          </div>
          <div>
            <Lbl>CUIT EMPLEADOR</Lbl>
            <Inp name="cuitEmpleador" value={form.cuitEmpleador || ''} onChange={onChange} placeholder="XX XXXXXXXX X" />
          </div>
          <div>
            <Lbl>TELÉFONO EMPLEADOR</Lbl>
            <Inp name="telefonoEmpleador" value={form.telefonoEmpleador || ''} onChange={onChange} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl>DOMICILIO EMPLEADOR</Lbl>
            <Inp name="domicilioEmpleador" value={form.domicilioEmpleador || ''} onChange={onChange} />
          </div>
          <div>
            <Lbl>EMAIL EMPLEADOR</Lbl>
            <Inp name="emailEmpleador" value={form.emailEmpleador || ''} onChange={onChange} placeholder="ejemplo@empresa.com" type="email" />
          </div>
        </div>
      </div>
    </Card>

    {/* ORIGEN DE FONDOS */}
    <Card title="ORIGEN DE FONDOS" icon="account_balance_wallet">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {ORIGEN_FUENTES.map(src => (
            <label key={src} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.origenFondos?.[src]}
                onChange={() => onCheckbox(src)}
                className="w-4 h-4 accent-honda-600"
              />
              {src}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl>OTRO (ESPECIFICAR)</Lbl>
            <Inp name="origenOtro" value={form.origenOtro || ''} onChange={onChange} />
          </div>
          <div>
            <Lbl required>INGRESOS NETOS TOTALES $</Lbl>
            <MoneyInput name="ingresosNetosTotal" value={form.ingresosNetosTotal} onChange={onCurrency} />
          </div>
        </div>
      </div>
    </Card>
  </>
);

// ─── PERSONA JURÍDICA FORM ──────────────────────────────────────────────────
const PersonaJuridicaForm = ({ form, onChange, onDate, onCurrency, onTipoPersonaChange, setForm }) => (
  <>
    {/* DATOS DEL COMPRADOR */}
    <Card title="DATOS DEL COMPRADOR" icon="business">
      <div className="space-y-3">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <Lbl>TIPO DE PERSONA</Lbl>
            <Sel name="tipoPersona" value={form.tipoPersona || ''} onChange={onTipoPersonaChange}>
              <option value="">Seleccione...</option>
              <option value="FISICA">Persona Física</option>
              <option value="JURIDICA">Persona Jurídica</option>
            </Sel>
          </div>
          <div className="col-span-8">
            <Lbl required>DNI / CUIT</Lbl>
            <Inp name="dniCuit" value={form.dniCuit || ''} onChange={onChange} />
          </div>
        </div>
        <div>
          <Lbl required>NOMBRE EMPRESA</Lbl>
          <Inp name="nombreEmpresa" value={form.nombreEmpresa || ''} onChange={onChange} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl required>FECHA DE INSCRIPCIÓN</Lbl>
            <Inp name="fechaInscripcion" value={form.fechaInscripcion || ''} onChange={onDate} placeholder="dd/mm/aaaa" maxLength="10" />
          </div>
          <div>
            <Lbl required>TIPO DE EMPRESA</Lbl>
            <Sel name="tipoEmpresa" value={form.tipoEmpresa || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              {TIPOS_EMPRESA.map(t => <option key={t} value={t}>{t}</option>)}
            </Sel>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl required>PEP (PERSONA EXPUESTA POLÍTICAMENTE)</Lbl>
            <Sel name="pep" value={form.pep || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              <option value="SI">Sí</option>
              <option value="NO">No</option>
            </Sel>
          </div>
          <div>
            <Lbl required>SUJETO OBLIGADO</Lbl>
            <Sel name="sujetoObligado" value={form.sujetoObligado || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              <option value="SI">Sí</option>
              <option value="NO">No</option>
            </Sel>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl required>INGRESOS COMPRADOR $</Lbl>
            <MoneyInput name="ingresosNetos" value={form.ingresosNetos} onChange={onCurrency} />
          </div>
        </div>
      </div>
    </Card>

    {/* DOMICILIO */}
    <DomicilioSection form={form} onChange={onChange} />

    {/* DATOS APODERADO */}
    <Card title="DATOS APODERADO" icon="person_pin">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl required>NOMBRE COMPLETO</Lbl>
            <Inp name="apodNombre" value={form.apodNombre || ''} onChange={onChange} />
          </div>
          <div>
            <Lbl required>DNI / DOCUMENTO</Lbl>
            <Inp name="apodDni" value={form.apodDni || ''} onChange={onChange} placeholder="Número de documento" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl required>FECHA DE NACIMIENTO</Lbl>
            <Inp name="apodFechaNacimiento" value={form.apodFechaNacimiento || ''} onChange={onDate} placeholder="dd/mm/aaaa" maxLength="10" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Lbl required>GÉNERO / SEXO</Lbl>
              <span className="text-xs text-red-600">
                (Como figura en el documento)
              </span>
            </div>

            <Sel
              name="apodGenero"
              value={form.apodGenero || ''}
              onChange={onChange}
            >
              <option value="">Seleccione...</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </Sel>
          </div>
          <div>
            <Lbl required>NACIONALIDAD</Lbl>
            <Inp name="apodNacionalidad" value={form.apodNacionalidad || ''} onChange={onChange} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl required>ESTADO CIVIL</Lbl>
            <Sel name="apodEstadoCivil" value={form.apodEstadoCivil || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              <option value="Soltero">Soltero/a</option>
              <option value="Casado">Casado/a</option>
            </Sel>
          </div>
          <div>
            <Lbl required>PEP (PERSONA EXPUESTA POLÍTICAMENTE)</Lbl>
            <Sel name="apodPep" value={form.apodPep || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              <option value="SI">Sí</option>
              <option value="NO">No</option>
            </Sel>
          </div>
          <div>
            <Lbl required>SUJETO OBLIGADO</Lbl>
            <Sel name="apodSujetoObligado" value={form.apodSujetoObligado || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              <option value="SI">Sí</option>
              <option value="NO">No</option>
            </Sel>
          </div>
        </div>
        {/* Domicilio del apoderado inline */}
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <Lbl required>CALLE</Lbl>
            <Inp name="apodCalle" value={form.apodCalle || ''} onChange={onChange} />
          </div>
          <div className="col-span-2">
            <Lbl required>NÚMERO</Lbl>
            <Inp name="apodNumero" value={form.apodNumero || ''} onChange={onChange} />
          </div>
          <div className="col-span-1">
            <Lbl>PISO</Lbl>
            <Inp name="apodPiso" value={form.apodPiso || ''} onChange={onChange} />
          </div>
          <div className="col-span-1">
            <Lbl>DEPTO</Lbl>
            <Inp name="apodDepto" value={form.apodDepto || ''} onChange={onChange} />
          </div>
          <div className="col-span-4">
            <Lbl required>LOCALIDAD</Lbl>
            <Inp name="apodLocalidad" value={form.apodLocalidad || ''} onChange={onChange} />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-3">
            <Lbl required>CÓDIGO POSTAL</Lbl>
            <Inp name="apodCodigoPostal" value={form.apodCodigoPostal || ''} onChange={onChange} />
          </div>
          <div className="col-span-5">
            <Lbl required>PROVINCIA</Lbl>
            <Sel name="apodProvincia" value={form.apodProvincia || ''} onChange={onChange}>
              <option value="">Seleccione...</option>
              {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
            </Sel>
          </div>
          <div className="col-span-4">
            <Lbl required>PAÍS</Lbl>
            <Inp name="apodPais" value={form.apodPais || ''} onChange={onChange} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Lbl required>TELÉFONO PARTICULAR</Lbl>
            <Inp name="apodTelefono" value={form.apodTelefono || ''} onChange={onChange} placeholder="Ej: 011 1234-5678" />
          </div>
          <div>
            <Lbl>TELÉFONO ALTERNATIVO</Lbl>
            <Inp name="apodTelefonoAlt" value={form.apodTelefonoAlt || ''} onChange={onChange} placeholder="Ej: 011 1234-5678" />
          </div>
          <div>
            <Lbl required>EMAIL</Lbl>
            <Inp name="apodEmail" value={form.apodEmail || ''} onChange={onChange} placeholder="ejemplo@gmail.com" type="email" />
          </div>
        </div>
      </div>
    </Card>

    {/* DATOS DEL CÓNYUGE DEL APODERADO - solo si estado civil es Casado */}
    {form.apodEstadoCivil === 'Casado' && (
      <FianceCard data={form} setData={setForm} prefix="apodConyuge" />
    )}
  </>
);

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────
const BuyerForm = () => {
  const [searchParams] = useSearchParams();
  const hash = searchParams.get('hash');

  const [status, setStatus] = useState('validating'); // 'validating' | 'valid' | 'invalid' | 'submitted'
  const [validationData, setValidationData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ tipoPersona: '' });

  const personType = form.tipoPersona; // 'FISICA' | 'JURIDICA' | ''

  // ── On mount: read hash and validate ──
  useEffect(() => {
    // hash null significa que el parámetro no existe en la URL
    if (hash === null) {
      setStatus('invalid');
      return;
    }
    // Modo de prueba local: hash=test omite la validación por API
    if (hash === 'test') {
      setStatus('valid');
      return;
    }
    validateBuyerLink({ hash })
      .then(data => {
        if (data?.message === 'NOT FOUND') {
          setStatus('invalid');
          return;
        }
        setValidationData(data);
        setStatus('valid');
      })
      .catch(() => setStatus('invalid'));
  }, [hash]);

  // ── Handlers ──
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleTipoPersonaChange = useCallback((e) => {
    const value = e.target.value;
    // Reset form but keep the new tipoPersona
    setForm({ tipoPersona: value });
  }, []);

  const handleDate = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: formatDateInput(value) }));
  }, []);

  const handleCurrency = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: formatCurrencyInput(value) }));
  }, []);

  const handleCheckbox = useCallback((src) => {
    setForm(prev => ({
      ...prev,
      origenFondos: { ...prev.origenFondos, [src]: !prev.origenFondos?.[src] },
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const requiredFieldsError = validateRequiredFields(form);
    if (requiredFieldsError) {
      setError(requiredFieldsError);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = buildPayload(form, validationData);
      await storeBuyerInformation(payload);
      setStatus('submitted');
    } catch (err) {
      console.error('[BuyerForm] submit error:', err);
      setError(err.message || 'Ocurrió un error al enviar los datos. Por favor intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  }, [form, validationData]);

  // ── Render: validating ──
  if (status === 'validating') {
    return (
      <HondaLoader
        message="Validando enlace..."
        subtitle="Por favor espere mientras verificamos su acceso."
        variant="fullscreen"
        size="large"
      />
    );
  }

  // ── Render: invalid ──
  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
        {/* Header */}
        <header className="gradient-red h-[60px] flex items-center px-6 shadow-lg">
          <img src="/logohonda.png" alt="Honda Plan" className="h-8 w-auto" onError={e => { e.target.style.display = 'none'; }} />
          <span className="text-white font-bold text-lg ml-2">HONDA Plan</span>
        </header>

        {/* Error content */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full bg-white border-2 border-gray-100 rounded-2xl shadow-soft overflow-hidden text-center">
            <div className="gradient-red p-6">
              <span className="material-icons text-white text-5xl">link_off</span>
            </div>
            <div className="p-8">
              <h2 className="text-xl font-bold text-gray-800 mb-3">Enlace no válido</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                El enlace de carga de datos ya no es válido o ha expirado.
                <br /><br />
                Por favor comuníquese con el titular del plan para solicitar un nuevo enlace
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white py-2 xl:py-3 mt-4 xl:mt-8">
          <div className="relative flex items-center justify-center w-full px-4 xl:px-6">
            <div className="absolute left-2 flex items-center gap-1 xl:gap-2">
              <span className="text-gray-400 text-[10px] xl:text-xs font-medium">powered by</span>
              <img src="/logoDatco.png" alt="Datco Logo" className="h-4 xl:h-5 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300" />
            </div>
            <p className="text-gray-400 text-[10px] xl:text-xs flex items-center gap-1 xl:gap-2">
              <span className="material-icons text-[10px] xl:text-xs">copyright</span>
              2026 Honda Motor Co., Ltd. - Todos los derechos reservados
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: submitted ──
  if (status === 'submitted') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
        <header className="gradient-red h-[60px] flex items-center px-6 shadow-lg">
          <img src="/logohonda.png" alt="Honda Plan" className="h-8 w-auto" onError={e => { e.target.style.display = 'none'; }} />
          <span className="text-white font-bold text-lg ml-2">HONDA Plan</span>
        </header>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden text-center">
            <div className="bg-green-600 p-6">
              <span className="material-icons text-white text-5xl">check_circle</span>
            </div>
            <div className="p-8">
              <h2 className="text-xl font-bold text-gray-800 mb-3">Solicitud enviada</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                Sus datos han sido enviados correctamente.<br />
                El vendedor recibira por mail el formulario de cesion 
              </p>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white py-2 xl:py-3 mt-4 xl:mt-8">
          <div className="relative flex items-center justify-center w-full px-4 xl:px-6">
            <div className="absolute left-2 flex items-center gap-1 xl:gap-2">
              <span className="text-gray-400 text-[10px] xl:text-xs font-medium">powered by</span>
              <img src="/logoDatco.png" alt="Datco Logo" className="h-4 xl:h-5 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300" />
            </div>
            <p className="text-gray-400 text-[10px] xl:text-xs flex items-center gap-1 xl:gap-2">
              <span className="material-icons text-[10px] xl:text-xs">copyright</span>
              2026 Honda Motor Co., Ltd. - Todos los derechos reservados
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: valid ── (show form)
  const commonFormProps = {
    form,
    onChange: handleChange,
    onDate: handleDate,
    onCurrency: handleCurrency,
    onCheckbox: handleCheckbox,
    onTipoPersonaChange: handleTipoPersonaChange,
    setForm,
  };

  return (
    <div className="w-screen min-h-screen overflow-auto bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      {/* Header */}
      <header className="gradient-red h-[60px] flex items-center px-6 shadow-lg flex-shrink-0">
        <img src="/logohonda.png" alt="Honda Plan" className="h-8 w-auto" onError={e => { e.target.style.display = 'none'; }} />
        <span className="text-white font-bold text-lg ml-2 tracking-wide">HONDA Plan</span>
      </header>

      {/* Page title */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <h1 className="text-honda-700 font-bold text-base uppercase tracking-wide flex items-center gap-2">
          <span className="material-icons text-honda-600 text-lg">assignment</span>
          SOLICITUD DE CESIÓN | DATOS DEL COMPRADOR
        </h1>
        <div className="flex items-center gap-3">
          {validationData?.group && (
            <div className="bg-gradient-to-br from-honda-50 to-white px-4 py-1.5 rounded-xl border border-honda-200 shadow-soft">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Grupo:</span>
              <span className="ml-1.5 text-sm font-bold text-honda-700">{validationData.group}</span>
            </div>
          )}
          {validationData?.order && (
            <div className="bg-gradient-to-br from-honda-50 to-white px-4 py-1.5 rounded-xl border border-honda-200 shadow-soft">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Orden:</span>
              <span className="ml-1.5 text-sm font-bold text-honda-700">{validationData.order}</span>
            </div>
          )}
          <span className="text-honda-700 text-xs font-semibold uppercase tracking-widest bg-honda-50 border border-honda-200 px-3 py-1 rounded-full">FORMULARIO</span>
        </div>
      </div>

      {/* Form body */}
      <main className="flex-1 px-6 py-5 w-full">

        {/* Persona type selector if not yet selected */}
        {!personType && (
          <Card title="DATOS DEL COMPRADOR" icon="person">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-4">
                <Lbl>TIPO DE PERSONA</Lbl>
                <Sel name="tipoPersona" value="" onChange={handleTipoPersonaChange}>
                  <option value="">Seleccione...</option>
                  <option value="FISICA">Persona Física</option>
                  <option value="JURIDICA">Persona Jurídica</option>
                </Sel>
              </div>
            </div>
          </Card>
        )}

        {personType === 'FISICA' && (
          <PersonaFisicaForm {...commonFormProps} />
        )}

        {personType === 'JURIDICA' && (
          <PersonaJuridicaForm {...commonFormProps} />
        )}

        {/* Submit button */}
        {personType && (
          <div className="flex flex-col items-end gap-3 mt-4 mb-8">
            {error && (
              <div className="w-full flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <span className="material-icons text-red-500 text-base mt-0.5 flex-shrink-0">error_outline</span>
                <span>{error}</span>
              </div>
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-2.5 gradient-red hover:shadow-glow-red text-white text-sm font-semibold rounded-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed hover:scale-105 transform"
            >
              {submitting ? 'Enviando...' : 'Enviar Solicitud'}
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white py-2 xl:py-3 mt-4 xl:mt-8">
        <div className="relative flex items-center justify-center w-full px-4 xl:px-6">
          <div className="absolute left-2 flex items-center gap-1 xl:gap-2">
            <span className="text-gray-400 text-[10px] xl:text-xs font-medium">powered by</span>
            <img src="/logoDatco.png" alt="Datco Logo" className="h-4 xl:h-5 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300" />
          </div>
          <p className="text-gray-400 text-[10px] xl:text-xs flex items-center gap-1 xl:gap-2">
            <span className="material-icons text-[10px] xl:text-xs">copyright</span>
            2026 Honda Motor Co., Ltd. - Todos los derechos reservados
          </p>
        </div>
      </div>
    </div>
  );
};

export default BuyerForm;
